import type { Agent, AgentRole, Persisted } from '@tunasong/schemas'

/**
 * Role-keyed defaults for the empty agent-chat header. Used when an agent
 * authored without a `roleLine` lands in the sidebar — we still want to
 * give the user a one-line "who am I talking to" cue and a few starter
 * prompts rather than a blank surface.
 *
 * Plugins that ship their own AgentDefinition should seed `roleLine` and
 * `examplePrompts` directly on the agent; this table is the safety net
 * for ad-hoc agents and the four schema roles.
 */
interface RoleDefaults {
  /** One-line "what does this agent do" cue. */
  roleLine: string
  /** Up to 3 starter prompts surfaced as chips. */
  prompts: string[]
}

// Prompts curated by Bowie (music-theory consultant) for breadth — one
// harmony angle, one lyric/prosody angle, one performance angle for
// song-agent; album-level concerns for project-agent; content-neutral
// for folder + generic since those may not be musical at all.
const roleDefaults: Record<AgentRole, RoleDefaults> = {
  'song-agent': {
    roleLine: 'A songwriting collaborator for this song.',
    prompts: [
      'Suggest a lifted bridge in a new key',
      "Tighten the second verse lyric to match verse 1's meter",
      'Propose a feel and tempo for a live arrangement',
    ],
  },
  'project-agent': {
    roleLine: 'An assistant for this project.',
    prompts: [
      'Sequence these songs into a coherent set list',
      'Find a unifying key or modal centre across the project',
      'Spot lyrical or thematic threads I can lean into',
    ],
  },
  'folder-agent': {
    roleLine: 'A library assistant for this folder.',
    prompts: [
      "Summarise what's in this folder",
      'Find related pages I should link together',
      'Draft an outline from these notes',
    ],
  },
  generic: {
    roleLine: 'A general-purpose assistant.',
    prompts: ['What can you help me with here?', 'Summarise the current page', 'Suggest next steps for this work'],
  },
}

/**
 * `definitionId`-keyed overrides. These are tighter, more specific prompts
 * for plugins that have shipped their own AgentDefinition. Plugins that
 * want full control over the chips should seed `agent.examplePrompts`
 * directly (that wins over both this map and the role-keyed fallback).
 */
const definitionDefaults: Record<string, string[]> = {
  'chord-advisor': [
    'My verse goes C – Am – F – G. Reharmonize it more interestingly but keep the same bass feel.',
    'Add a secondary dominant before the F in this progression and tell me what it is.',
    "Is the Eb in my C-major chorus borrowed? Explain what it's doing and what it sets up.",
  ],
  'song-collaborator': [
    "I have two verses and a chorus. What's the strongest next section to add, and why?",
    'My bridge feels same-y as the verse. Propose a bridge that actually contrasts.',
    'Critique my structure — where does it sag, and what would you cut or add?',
  ],
}

/**
 * Role-keyed default colors. Joni's choices for the material palette:
 *  - song-agent → blue (core, signals "this is central")
 *  - project-agent → purple (organizational / planning)
 *  - folder-agent → grey (content-neutral)
 *  - generic → green (fresh / helpful default)
 *
 * Names match `@tunasong/ui-lib`'s `getColorCSS` registry. Unknown roles
 * fall back to `generic`'s green at resolve time.
 */
const roleDefaultColors: Record<AgentRole, string> = {
  'song-agent': 'blue',
  'project-agent': 'purple',
  'folder-agent': 'grey',
  generic: 'green',
}

/**
 * Role-keyed default icon registry keys. Bowie picked the semantic concept
 * per role; the concrete registry key matches the closest neighbour in
 * `@tunasong/icons`:
 *  - song-agent → MusicNote (treble-clef-ish; the registry alias for Music)
 *  - project-agent → LibraryMusic (collection-of-songs; closest semantic
 *    neighbour to the spec's "folder-with-star / briefcase" — the registry
 *    has no Work or BookmarksOutlined)
 *  - folder-agent → Folder (plain folder; literal match)
 *  - generic → SmartButton (the registry's Sparkles alias; closest to the
 *    spec's "sparkle / robot" — registry has no AutoAwesome or SmartToy)
 *
 * Unknown roles fall back to `generic`'s SmartButton at resolve time.
 */
const roleDefaultIcons: Record<AgentRole, string> = {
  'song-agent': 'MusicNote',
  'project-agent': 'LibraryMusic',
  'folder-agent': 'Folder',
  generic: 'SmartButton',
}

const trim = (s: string | undefined): string => s?.trim() ?? ''

/**
 * Resolve the header line for an agent. Order: explicit `roleLine` first,
 * then the role-keyed default. Falls back to an empty string if `role` is
 * outside the schema enum (defensive — the schema parser should already
 * reject that case at ingest time).
 */
export const resolveDefaultRoleLine = (agent: Persisted<Agent> | Agent): string => {
  const explicit = trim(agent.roleLine)
  if (explicit.length > 0) {
    return explicit
  }
  return roleDefaults[agent.role]?.roleLine ?? ''
}

/**
 * Resolve up to 3 starter prompts. Priority order:
 * 1. agent.examplePrompts (whitespace-only entries dropped)
 * 2. definitionId-keyed defaults
 * 3. role-keyed defaults
 * 4. empty array (unknown role + nothing else seeded)
 */
export const resolveDefaultPrompts = (agent: Persisted<Agent> | Agent): string[] => {
  const explicit = (agent.examplePrompts ?? []).map(trim).filter(p => p.length > 0)
  if (explicit.length > 0) {
    return explicit.slice(0, 3)
  }
  const byDefinition = agent.definitionId ? definitionDefaults[agent.definitionId] : undefined
  if (byDefinition && byDefinition.length > 0) {
    return byDefinition.slice(0, 3)
  }
  const byRole = roleDefaults[agent.role]?.prompts
  if (byRole && byRole.length > 0) {
    return byRole.slice(0, 3)
  }
  return []
}

/**
 * Resolve the agent's display color. Order:
 *  1. agent.color (trimmed) when non-empty
 *  2. role-keyed default
 *  3. generic's default ("green") when the role is outside the schema enum
 *
 * Returns a plain string — the material color name. The consumer hands it
 * to `getColorCSS()` (in `@tunasong/ui-lib`) which gracefully resolves
 * unknown names to `inherit`.
 */
export const resolveDefaultColor = (agent: Persisted<Agent> | Agent): string => {
  const explicit = trim(agent.color)
  if (explicit.length > 0) {
    return explicit
  }
  return roleDefaultColors[agent.role] ?? roleDefaultColors.generic
}

/**
 * Resolve the agent's display icon registry key. Same fallback chain as
 * `resolveDefaultColor`. The consumer looks the key up in `@tunasong/icons`'
 * registry; unknown keys fall back to a sensible default at the consumer.
 */
export const resolveDefaultIconKey = (agent: Persisted<Agent> | Agent): string => {
  const explicit = trim(agent.iconKey)
  if (explicit.length > 0) {
    return explicit
  }
  return roleDefaultIcons[agent.role] ?? roleDefaultIcons.generic
}
